import axios, { AxiosInstance, AxiosResponse } from "axios";
import { IExternalQuote, IVehicle, IViolation } from "./ExternalQuote";
import { IRealTimeAutoResult, IDriverBase, ICreditCardInfo, IContactInfo, IAdditionalDriver } from "./Domain";
import { successResponseInterceptor, errorResponseInterceptor } from "../infrastructure/global";

export default class BravoApiClient {

    private _instance: AxiosInstance;

    constructor() {
        this._instance = axios.create({
            baseURL: '/d2c/bravo',
            method: 'post'
        });

        this._instance.interceptors.response.use(
            successResponseInterceptor,
            errorResponseInterceptor
        );
    }

    createtQuote(selectedRate: IRealTimeAutoResult): Promise<AxiosResponse<any>> {
        return this._instance.request({
            url: '/create-quote',
            data: selectedRate
        });
    }

    getQuote(quoteId: number): Promise<AxiosResponse<IExternalQuote>> {
        return this._instance.request({
            url: '/get-quote/' + quoteId,
            method: 'get'
        });
    }

    getLienholders(query: string) {
        return this._instance.request({
            url: '/get-lienholders?query=' + query,
            method: 'get'
        });
    }

    updateVehicle(quoteId: number, paymentPlanCode: number, vehicle: IVehicle, updateLienholder: boolean = false, updateGaragingAddress: boolean = false) {
        const data = {
            quoteId,
            vehicleId: vehicle.id,
            vin: vehicle.vin,
            paymentPlanCode,
            updateLienholder,
            hasLienholder: vehicle.hasLienholder,
            lienholderName: vehicle.lienholder?.name,
            lienholderAddress: vehicle.lienholder?.address?.street,
            lienholderCity: vehicle.lienholder?.address?.city,
            lienholderZip: vehicle.lienholder?.address?.zipCode,
            lienholderStateCd: vehicle.lienholder?.address?.stateCode,
            updateGaragingAddress,
            garagingStreet : vehicle.garagingAddress?.street,
            garagingCity : vehicle.garagingAddress?.city,
            garagingState : vehicle.garagingAddress?.stateCode,
            garagingZipcode : vehicle.garagingAddress?.zipCode,
            wasSalvaged: vehicle.wasSalvaged
        };
        return this._instance.request({
            url: '/update-vehicle',
            data
        });
    }

    updateDriver(quoteId: number, paymentPlanCode: number, driver: IDriverBase) {
        const data = {
            quoteId,
            driverId: driver.id,
            paymentPlanCode,
            firstName: driver.firstName,
            lastName: driver.lastName,
            licenseNumber: driver.licenseNumber,
            licenseState: driver.licenseState,
            birthDate: driver.birthDate,
            isInsured: driver.isInsured,
        };
        return this._instance.request({
            url: '/update-driver',
            data
        });
    }

    saveCreditCardInfo(quoteId: number, creditCardInfo: ICreditCardInfo) {
        
        const data = { 
            ...creditCardInfo,
            quoteId: parseInt(quoteId.toString()),
            useForInstallments: creditCardInfo.savePaymentMethod
        };
            
        return this._instance.request({
            url: '/save-cc-info',
            data
        });
    }

    getViolations(quoteId: number) {
        return this._instance.request({
            url: '/get-violations/' + quoteId,
            method: 'get'
        });
    }

    reRateQuote(quoteId: number, paymentPlanCode: number) {
        return this._instance.request({
            url: '/re-rate',
            data: {
                quoteId: parseInt(quoteId.toString(), 10),
                paymentPlanCode
            }
        });
    }

    reSetQuoteEffDate(quoteId: number, paymentPlanCode: number, effectiveDate: string) {
        return this._instance.request({
            url: '/reset-eff-date',
            data: {
                quoteId: parseInt(quoteId.toString(), 10),
                paymentPlanCode,
                effectiveDate
            }
        });
    }

    getPaymentInfo(quoteId: number) {
        return this._instance.request({
            url: '/get-pmt-info/' + quoteId,
            method: 'get'
        });
    }

    disputeViolations(quoteId: number, driverId: number, violationsForDispute: Array<IViolation>) {
        const data = {
            quoteId: parseInt(quoteId.toString()),
            driverId,
            violationsForDispute
        };

        return this._instance.request({
            url: '/dispute-violations',
            data
        });
    }

    bindQuote(quoteId: number) {
        return this._instance.request({
            url: '/bind-quote/' + quoteId,
            method: 'get'
        });
    }

    getDocuments(quoteId: number) {
        return this._instance.request({
            url: '/get-documents/' + quoteId,
            method: 'get'
        });
    }

    getVehicle(quoteId: number, vehicleId: number) {
        return this._instance.request({
            url: '/get-vehicle/' + quoteId + '/' + vehicleId,
            method: 'get'
        });
    }

    getESignatureForms(rtrQuoteId: number) {
        return this._instance.request({
            url: '/get-esig-forms/' + rtrQuoteId,
            method: 'get'
        });
    }

    updateESignatureAgreement(rtrQuoteId: number, esignatureCode: string, insuredName: string, needToSign: boolean, lastForm: boolean, options: any[]) {

        const data = {
            rtrQuoteId,
            code: esignatureCode,
            insuredName,
            needToSign,
            lastForm,
            options
        };

        return this._instance.request({
            url: '/update-esig-agreement',
            data
        });
    }


    updateContactInfo(rtrQuoteId: number, contactInfo: IContactInfo) {
        return this._instance.request({
            url: '/update-contact-fq',
            data: {
                rtrQuoteId,
                email: contactInfo.email,
                phone: contactInfo.phone
            }
        });
    }

    updateAdditionalDrivers(rtrQuoteId: number, additionalDrivers: Array<IAdditionalDriver>) {
        return this._instance.request({
            url: '/update-additional-drivers',
            data: {
                rtrQuoteId,
                additionalDrivers
            }
        });
    }
}
